// Minimal gettext/ngettext returning PLAIN strings with `%(name)s` interpolation.
//
// eb-ui used `@eventbrite/i18n` whose `gettext` returns a lazy `GenericLazyString`
// object that is unsafe to render directly in React 19 and has no `ngettext`. Per
// harness/memory.md the sanctioned replacement is a tiny local gettext backed by a
// module-level active-messages map (set in `_app` from `pageProps.messages`) plus
// react-intl for provider-driven components (global header / footer). trending
// ships no translation catalog today (en_US source strings only), so with an empty
// map these just return the source string — exactly faithful to eb-ui at en_US.

type InterpolationParams = Record<string, string | number>;

let activeMessages: Record<string, string> = {};

/** Set the active translation catalog (called from `_app` per request). */
export function setGettextMessages(messages?: Record<string, string>): void {
    activeMessages = messages || {};
}

function interpolate(template: string, params?: InterpolationParams): string {
    if (!params) return template;
    return template.replace(/%\((\w+)\)s/g, (_match, key: string) =>
        key in params ? String(params[key]) : `%(${key})s`,
    );
}

/** Translate + interpolate a source string. Returns the source string if untranslated. */
export function gettext(text: string, params?: InterpolationParams): string {
    return interpolate(activeMessages[text] ?? text, params);
}

/** Plural-aware gettext. `count` is also exposed to interpolation as `%(count)s`. */
export function ngettext(
    singular: string,
    plural: string,
    count: number,
    params?: InterpolationParams,
): string {
    const base = count === 1 ? singular : plural;
    return gettext(base, { count, ...params });
}
